import { html, nothing } from "lit";
import type { HostEnvironmentInfo, SessionEnvironmentExplainResult } from "../types.ts";
import { environmentBadge } from "./session-environment-panel.ts";

export type ChatEnvironmentPanelProps = {
  loading: boolean;
  error: string | null;
  data: SessionEnvironmentExplainResult | null;
  onRefresh: () => void;
};

function primaryIp(host: HostEnvironmentInfo): string {
  const nic = host.networks.find((n) => n.family === "IPv4");
  return nic?.address ?? "—";
}

function formatKb(kb: number): string {
  if (!Number.isFinite(kb) || kb <= 0) return "0";
  const units = ["K", "M", "G", "T", "P"];
  let value = kb;
  let u = 0;
  while (value >= 1024 && u < units.length - 1) {
    value /= 1024;
    u += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)}${units[u]}`;
}

function renderSection(label: string, content: unknown) {
  return html`<div class="chat-env-section">
    <dt>${label}</dt>
    <dd class="mono">${content}</dd>
  </div>`;
}

function renderHost(host: HostEnvironmentInfo) {
  return html`
    <dl class="chat-env-grid">
      ${renderSection("Hostname", host.hostname)}
      ${renderSection(
        "OS",
        `${host.osDistro ?? host.osPlatform} · kernel ${host.kernel ?? host.arch}`,
      )}
      ${renderSection("Gateway", `${host.gatewayVersion ?? "unknown"} · node ${host.nodeVersion}`)}
      ${renderSection("Uptime", `${host.uptimeSec}s`)}
      ${renderSection("Effective user", `${host.effectiveUser} (uid ${host.user.uid})`)}
      ${renderSection("CWD", host.cwd)}
    </dl>
  `;
}

function renderPermissions(host: HostEnvironmentInfo, data: SessionEnvironmentExplainResult) {
  return html`
    <dl class="chat-env-grid">
      ${renderSection("Sandbox", environmentBadge(data))}
      ${renderSection("Sudo", host.hasSudo ? "yes" : "no")}
      ${renderSection("Root", host.permissions.isRoot ? "yes" : "no")}
      ${renderSection("Tools allowed", String(data.toolPolicyAllow.length))}
      ${renderSection("Tools blocked", String(data.toolPolicyDeny.length))}
    </dl>
  `;
}

function renderDisks(host: HostEnvironmentInfo) {
  if (!host.disks.length) {
    return html`
      <p class="muted">No disks (df missing or filtered).</p>
    `;
  }
  return html`<table class="chat-env-table">
    <thead><tr><th>FS</th><th class="num">Size</th><th class="num">Used</th><th class="num">Avail</th><th class="num">Use%</th><th>Mount</th></tr></thead>
    <tbody>${host.disks.map(
      (d) => html`<tr>
        <td>${d.filesystem}</td>
        <td class="num">${formatKb(d.sizeKb)}</td>
        <td class="num">${formatKb(d.usedKb)}</td>
        <td class="num">${formatKb(d.availKb)}</td>
        <td class="num">${d.usePct}%</td>
        <td>${d.mount}</td>
      </tr>`,
    )}</tbody>
  </table>`;
}

function renderNetworks(host: HostEnvironmentInfo, sandboxNetwork: string) {
  if (!host.networks.length && !sandboxNetwork) {
    return html`
      <p class="muted">No external interfaces detected.</p>
    `;
  }
  return html`<dl class="chat-env-grid">
    ${host.networks.map(
      (n) => html`<div class="chat-env-section">
        <dt>${n.name}</dt>
        <dd>${n.family} · ${n.address}${n.cidr ? html`/${n.cidr}` : nothing} · ${n.mac ?? "—"}</dd>
      </div>`,
    )}
    ${
      sandboxNetwork
        ? html`<div class="chat-env-section">
          <dt>sandbox-bridge</dt>
          <dd>Docker · ${sandboxNetwork}</dd>
        </div>`
        : nothing
    }
  </dl>`;
}

function renderUsers(host: HostEnvironmentInfo) {
  if (!host.users.length) {
    return html`
      <p class="muted">No active sessions detected.</p>
    `;
  }
  return html`<ul class="chat-env-pills">
    ${host.users.map((u) => html`<li class="chat-env-pill">${u}</li>`)}
  </ul>`;
}

function renderWarnings(host: HostEnvironmentInfo) {
  if (!host.warnings.length) return nothing;
  return html`<details class="chat-env-warnings">
    <summary>${host.warnings.length} host probe warning(s)</summary>
    <ul>${host.warnings.map((w) => html`<li><code>${w}</code></li>`)}</ul>
  </details>`;
}

export function renderChatEnvironmentPanel(props: ChatEnvironmentPanelProps) {
  if (props.loading && !props.data) {
    return html`
      <div class="chat-env-panel" aria-busy="true">
        <p class="muted">Loading environment…</p>
      </div>
    `;
  }
  if (props.error && !props.data) {
    return html`<div class="chat-env-panel" data-testid="chat-env-panel">
      <p class="chat-env-warning">${props.error}</p>
      <button type="button" class="btn subtle" @click=${props.onRefresh}>Retry</button>
    </div>`;
  }
  if (!props.data || !props.data.host) {
    return html`<div class="chat-env-panel" data-testid="chat-env-panel">
      <p class="muted">
        Environment data isn't loaded yet.
        <button type="button" class="btn subtle" @click=${props.onRefresh}>Load now</button>
      </p>
    </div>`;
  }
  const host = props.data.host;
  const badge = environmentBadge(props.data);
  return html`<div class="chat-env-panel" data-testid="chat-env-panel">
    <details open>
      <summary>
        <span>${host.hostname}</span>
        <span class="chat-env-pill">${badge}</span>
        <span class="muted">${primaryIp(host)}</span>
        <span class="muted">${`${host.disks.length} disk`}</span>
        <span class="muted">${`${host.users.length} user`}</span>
        <button
          type="button"
          class="btn subtle"
          @click=${props.onRefresh}
          ?disabled=${props.loading}
        >${props.loading ? "Refreshing…" : "Refresh"}</button>
      </summary>
      <dl class="chat-env-grid">
        <h4 class="chat-env-section-title">Host</h4>
        ${renderHost(host)}
        <h4 class="chat-env-section-title">Permissions</h4>
        ${renderPermissions(host, props.data)}
        <h4 class="chat-env-section-title">Disks</h4>
        ${renderDisks(host)}
        <h4 class="chat-env-section-title">Networks</h4>
        ${renderNetworks(host, props.data.network)}
        <h4 class="chat-env-section-title">Logged-in users</h4>
        ${renderUsers(host)}
        ${renderWarnings(host)}
      </dl>
    </details>
  </div>`;
}
